import React, { useState, useEffect } from 'react';
import { Box, styled, Typography, Avatar, Menu, MenuItem, ListItemIcon, Divider, Paper, IconButton, Drawer, List, ListItemText, InputBase, alpha, Collapse, ListItemButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import logoLight from '../assets/images/light/Logo.png';
import logoDark from '../assets/images/dark/Logo_dark.png';
import platformDeepDiveIconLight from '../assets/images/light/platform_deep_dive.png';
import platformDeepDiveIconDark from '../assets/images/dark/platform_deep_dive-dark.png';
import supportManagementIconLight from '../assets/images/light/support_management.png';
import supportManagementIconDark from '../assets/images/dark/support_management-dark.png';
import monitoringIconLight from '../assets/images/light/monitoring.png';
import monitoringIconDark from '../assets/images/dark/monitoring-dark.png';
import cloudIconLight from '../assets/images/light/Cloud.png';
import cloudIconDark from '../assets/images/dark/Cloud-dark.png';
import serviceConfigIconLight from '../assets/images/light/service_configuration.png';
import serviceConfigIconDark from '../assets/images/dark/service_configuration-dark.png';
import securityIconLight from '../assets/images/light/security.png';
import securityIconDark from '../assets/images/dark/security-dark.png';
import cicdIconLight from '../assets/images/light/cicd.png';
import cicdIconDark from '../assets/images/dark/cicd-dark.png';
import qaTestingIconLight from '../assets/images/light/QA_dashboard.png';
import qaTestingIconDark from '../assets/images/dark/QA_dashboard-dark.png';
import burgerIconLight from '../assets/icons/light/burger icon.png';

import notifyIconLight from '../assets/icons/light/Notify.png';
import notifyIconDark from '../assets/icons/dark/Notify_dark.png';
import userIconLight from '../assets/icons/light/User.png';
import userIconDark from '../assets/icons/dark/User_dark.png';
import homeMenuIconLight from '../assets/icons/light/home-icon.png';
import homeMenuIconDark from '../assets/icons/dark/home-icon-dark.png';
import userConfigIconLight from '../assets/images/light/user-config.png';
import userConfigIconDark from '../assets/images/dark/user-config-dark.png';
import modulesIconLight from '../assets/icons/light/modules-icon.png';
import modulesIconDark from '../assets/icons/dark/modules-icon-dark.png';
import lightDarkIconLight from '../assets/icons/light/light-dark-icon.png';
import lightDarkIconDark from '../assets/icons/dark/light_dark-dark.png';
import toggleLight from '../assets/icons/light/Toggle.png';
import toggleDark from '../assets/icons/dark/Toggle_dark.png';
import settingsIconLight from '../assets/icons/light/settings-icon.png';
import settingsIconDark from '../assets/icons/dark/settings-icon-dark.png';

import './Sidebar.css';

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 30px',
  height: '86px',
  width: '100%',
  maxWidth: '1440px',
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  margin: '0 auto' // Center the container
}));

const LogoSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
});

const SearchContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '20px',
  backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.15) : alpha(theme.palette.grey[200], 0.8),
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.25) : alpha(theme.palette.grey[300], 0.8),
  },
  width: '240px',
  height: '36px',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.mode === 'dark' ? '#1674B4' : theme.palette.text.secondary,
}));

const VerticalDivider = styled(Box)(({ theme }) => ({
  height: '18px',
  width: '1px',
  backgroundColor: theme.palette.mode === 'dark' ? '#535353' : '#B1B1B1',
  margin: theme.spacing(0, 1.5),
}));



const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
  },
}));

const ProfileMenu = styled(Paper)({
  marginTop: '8px',
  minWidth: '200px'
});

const ProfileMenuItem = styled(MenuItem)({
  padding: '10px 16px'
});

const ProfileHeader = styled(Box)({
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px'
});

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: '60px',
  height: '60px',
  marginBottom: '8px',
  color: theme.palette.text.primary
}));

const ProfileName = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: '8px',
  textAlign: 'center',
  color: theme.palette.text.primary
}));

const ProfileEmail = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '14px',
  textAlign: 'center'
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 2),
  ...theme.mixins.toolbar,
  justifyContent: 'space-between',
}));

const DrawerListItem = styled(ListItemButton)(({ theme }) => ({
  padding: '12px 24px',
  '& .MuiListItemIcon-root': {
    minWidth: '40px',
  },
  '& .MuiListItemText-primary': {
    fontSize: '16px',
    fontWeight: '500',
    color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
  },
}));

const DrawerSubListItem = styled(ListItemButton)(({ theme }) => ({
  padding: '10px 24px 10px 64px',
  '& .MuiListItemIcon-root': {
    minWidth: '40px',
    color: theme.palette.primary.main,
  },
  '& .MuiListItemText-primary': {
    fontSize: '14px',
    color: theme.palette.mode === 'dark' ? '#FFFFFF' : theme.palette.text.secondary,
  },
}));

function Sidebar({ isAdmin, darkMode, handleThemeChange }) {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modulesOpen, setModulesOpen] = useState(false);
  const [allowedSections, setAllowedSections] = useState([]);

  useEffect(() => {
    const email = localStorage.getItem('user_email') || '';
    setUserEmail(email);
    if (email) {
      const namePart = email.split('@')[0];
      const formattedName = namePart
        .replace(/[._]/g, ' ')
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
      setUserName(formattedName);
    }

    const fetchAllowedSections = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const API_URL = process.env.REACT_APP_API_URL;
          const response = await axios.get(`${API_URL}/api/user-access/my-sections`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAllowedSections(response.data || []);
        } catch (error) {
          console.error('Error fetching allowed sections:', error.response ? error.response.data : error.message);
          setAllowedSections([]);
        }
      }
    };
    fetchAllowedSections();
  }, []);

  const userInitial = userName ? userName.charAt(0).toUpperCase() : '';

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_email');
    navigate('/login');
    handleClose();
  };

  const handleRestrictedAccess = () => {
    toast.error(
      '⚠️ You are restricted to access this, please contact Admin Team',
      {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        icon: false,
        style: {
          backgroundColor: "#FF474C",
          color: "black",
          fontWeight: "bold",
          borderRadius: "8px",
          padding: "12px",
          fontSize: "14px",
        },
      }
    );
    setDrawerOpen(false);
  };

  const sectionMap = {
    '/monitoring-dashboard': 'monitoring',
    '/cloud': 'cloud_management',
    '/database': 'service_configuration',
    '/security-dashboard': 'security',
    '/development': 'cicd_flows',
    '/qa-dashboard': 'qa_testing',
  };

  const handleNavigate = (path) => {
    if (path === '/user-configuration') {
      if (isAdmin) {
        navigate(path);
        setDrawerOpen(false);
      } else {
        handleRestrictedAccess();
      }
      return;
    }

    const requiredSection = sectionMap[path];
    if (!requiredSection || isAdmin || allowedSections.includes(requiredSection)) {
      navigate(path);
      setDrawerOpen(false);
    } else {
      handleRestrictedAccess();
    }
  };

  const toggleDrawer = (openState) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(openState);
  };

  const handleModulesClick = () => {
    setModulesOpen(!modulesOpen);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <HeaderContainer className="header-container">
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: (theme) => ({
            width: 320,
            height: modulesOpen ? 565 : 355,
            transition: theme.transitions.create('height', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            maxHeight: '90vh',
            overflowY: 'auto',
            borderRadius: '0px 8px 8px 0px',
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            backgroundColor: theme.palette.mode === 'dark' ? '#004370' : theme.palette.background.paper,
          })
        }}
      >
        <DrawerHeader>
          <IconButton onClick={toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
          <img src={darkMode ? logoDark : logoLight} alt="logo" style={{ height: 40 }} />
        </DrawerHeader>
        <List component="nav" sx={{ p: 2 }}>
          <DrawerListItem onClick={() => handleNavigate('/home')}>
            <ListItemIcon>
              <img src={darkMode ? homeMenuIconDark : homeMenuIconLight} alt="Home" style={{ width: 24, height: 24 }} />
            </ListItemIcon>
            <ListItemText primary="Home" />
          </DrawerListItem>
          <Divider sx={theme => ({ borderColor: theme.palette.mode === 'dark' ? '#5FBDF1' : '#CEEEFF' })} />


          <DrawerListItem onClick={handleModulesClick}>
            <ListItemIcon>
              <img src={darkMode ? modulesIconDark : modulesIconLight} alt="Modules" style={{ width: 24, height: 24 }} />
            </ListItemIcon>
            <ListItemText primary="Modules" />
            {modulesOpen ? <ExpandLess /> : <ExpandMore />}
          </DrawerListItem>
          <Collapse in={modulesOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <DrawerSubListItem onClick={() => handleNavigate('/support-dashboard')}><ListItemIcon><img src={darkMode ? supportManagementIconDark : supportManagementIconLight} alt="Support Management" style={{ width: 24, height: 24 }} /></ListItemIcon><ListItemText primary="Support Management" /></DrawerSubListItem>
              <DrawerSubListItem onClick={() => handleNavigate('/monitoring-dashboard')}><ListItemIcon><img src={darkMode ? monitoringIconDark : monitoringIconLight} alt="Monitoring" style={{ width: 24, height: 24 }} /></ListItemIcon><ListItemText primary="Monitoring" /></DrawerSubListItem>
              <DrawerSubListItem onClick={() => handleNavigate('/cloud')}><ListItemIcon><img src={darkMode ? cloudIconDark : cloudIconLight} alt="Cloud" style={{ width: 24, height: 24 }} /></ListItemIcon><ListItemText primary="Cloud" /></DrawerSubListItem>
              <DrawerSubListItem onClick={() => handleNavigate('/database')}><ListItemIcon><img src={darkMode ? serviceConfigIconDark : serviceConfigIconLight} alt="Service Configuration" style={{ width: 24, height: 24 }} /></ListItemIcon><ListItemText primary="Service Configuration" /></DrawerSubListItem>
              <DrawerSubListItem onClick={() => handleNavigate('/security-dashboard')}><ListItemIcon><img src={darkMode ? securityIconDark : securityIconLight} alt="Security" style={{ width: 24, height: 24 }} /></ListItemIcon><ListItemText primary="Security" /></DrawerSubListItem>
              <DrawerSubListItem onClick={() => handleNavigate('/development')}><ListItemIcon><img src={darkMode ? cicdIconDark : cicdIconLight} alt="CI/CD" style={{ width: 24, height: 24 }} /></ListItemIcon><ListItemText primary="CI/CD Flows" /></DrawerSubListItem>
              <DrawerSubListItem onClick={() => handleNavigate('/qa-dashboard')}><ListItemIcon><img src={darkMode ? qaTestingIconDark : qaTestingIconLight} alt="QA & Testing" style={{ width: 24, height: 24 }} /></ListItemIcon><ListItemText primary="QA & Testing" /></DrawerSubListItem>
              <DrawerSubListItem onClick={() => handleNavigate('/user-configuration')}>
                <ListItemIcon><img src={darkMode ? userConfigIconDark : userConfigIconLight} alt="User Configuration" style={{ width: 24, height: 24 }} /></ListItemIcon>
                <ListItemText primary="User Configuration" />
              </DrawerSubListItem>
            </List>
          </Collapse>
          <Divider sx={theme => ({ borderColor: theme.palette.mode === 'dark' ? '#5FBDF1' : '#CEEEFF' })} />

          <Divider sx={theme => ({ borderColor: theme.palette.mode === 'dark' ? '#5FBDF1' : '#CEEEFF' })} />

          <DrawerListItem>
            <ListItemIcon>
              <img src={darkMode ? lightDarkIconDark : lightDarkIconLight} alt="Toggle Theme" style={{ width: 24, height: 24 }} />
            </ListItemIcon>
            <ListItemText primary="Light/Dark Mode" />
            <IconButton onClick={handleThemeChange} sx={{ p: 0 }}>
              <img src={darkMode ? toggleDark : toggleLight} alt="Theme toggle" style={{ height: '19px' }} />
            </IconButton>
          </DrawerListItem>
          <Divider sx={theme => ({ borderColor: theme.palette.mode === 'dark' ? '#5FBDF1' : '#CEEEFF' })} />

          <DrawerListItem onClick={() => handleNavigate('/platform-deep-dive')}>
            <ListItemIcon>
              <img src={darkMode ? platformDeepDiveIconDark : platformDeepDiveIconLight} alt="Platform Deep Dive" style={{ width: 24, height: 24 }} />
            </ListItemIcon>
            <ListItemText primary="Platform Deep Dive" />
          </DrawerListItem>
          <Divider sx={theme => ({ borderColor: theme.palette.mode === 'dark' ? '#5FBDF1' : '#CEEEFF' })} />
          <DrawerListItem onClick={() => handleNavigate('/settings')}>
            <ListItemIcon>
              <img src={darkMode ? settingsIconDark : settingsIconLight} alt="Settings" style={{ width: 24, height: 24 }} />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </DrawerListItem>
        </List>
      </Drawer>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton 
          color="inherit"
          aria-label="open drawer"
          onClick={toggleDrawer(true)}
          edge="start"
          sx={{ mr: 2, marginBottom: '20px' }}
        >
          <img src={burgerIconLight} alt="Menu" style={{ width: '15.56px', height: '12.44px' }} />
        </IconButton>
        <LogoSection onClick={() => navigate('/home')} style={{ cursor: 'pointer' }}>
          <img src={darkMode ? logoDark : logoLight} alt="DevOps Buddy Logo" style={{ width: '217px', height: '62.90px' }} />
        </LogoSection>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', marginBottom: '20px' }}>
        <SearchContainer sx={{ ml: 3 }}>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Search…"
            inputProps={{ 'aria-label': 'search' }}
          />
        </SearchContainer>

        <VerticalDivider />

        <IconButton color="inherit">
          <img src={darkMode ? notifyIconDark : notifyIconLight} alt="Notifications" style={{ width: '36px', height: '36px' }} />
        </IconButton>

        <VerticalDivider />

        <IconButton
          edge="end"
          aria-label="account of current user"
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          onClick={handleClick}
          color="inherit"
        >
          <img src={darkMode ? userIconDark : userIconLight} alt="User" style={{ width: '36px', height: '36px' }} />
        </IconButton>
        
        <Menu
          id="account-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            component: ProfileMenu,
            elevation: 3
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <ProfileHeader>
            <ProfileAvatar sx={{ bgcolor: '#1976d2' }}>
              {userInitial}
            </ProfileAvatar>
            <ProfileName>{userName}</ProfileName>
            <ProfileEmail>{userEmail}</ProfileEmail>
          </ProfileHeader>
          
          <Divider />
          
          <ProfileMenuItem onClick={() => handleNavigate('/home')}>
            <ListItemIcon>
              <HomeIcon fontSize="small" />
            </ListItemIcon>
            Home
          </ProfileMenuItem>
          
          <ProfileMenuItem onClick={() => handleNavigate('/profile')}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            Profile
          </ProfileMenuItem>
          
          <Divider />
          
          <ProfileMenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            <Typography color="error">Logout</Typography>
          </ProfileMenuItem>
        </Menu>
      </Box>
    </HeaderContainer>
  );
}

export default Sidebar;
